import { forwardRef, useEffect, useState } from "react";
import { Autocomplete, Button, createStyles, Flex, Group, InputBase, Modal, Select, Text, useMantineTheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ms2525d } from "mil-std-2525";
import Symbol from "milsymbol";
import { IMaskInput } from "react-imask";
import { replaceAt } from "../../common/replaceAt";
import {
	contextValues,
	DISMOUNTED_SYMBOLSET_VALUE,
	echelonValues,
	EQUIPMENT_SYMBOLSET_VALUE,
	HQTFDummyValues,
	leadershipValues,
	mobilityValues,
	sidValues,
	statusValues,
	SUBSURFACE_SYMBOLSET_VALUE,
	SURFACE_SYMBOLSET_VALUE,
	towedArrayValues,
	UNIT_SYMBOLSET_VALUE,
} from "./SelectsValues";

interface ISymbolPicker {
	side: number;
	defaultValue: string;
	onChange: (value: string) => void;
}

interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
	// image is canvas
	image: string;
	label: string;
	description: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(({ image, label, description, ...others }: ItemProps, ref) => (
	<div ref={ref} {...others}>
		<Group noWrap>
			<span dangerouslySetInnerHTML={{ __html: image }} />
			<div>
				<Text size="sm">{label}</Text>
			</div>
		</Group>
	</div>
));

export const SymbolPicker: React.FC<ISymbolPicker> = ({ side, defaultValue, onChange }) => {
	const { classes } = useStyles();
	const theme = useMantineTheme();
	const [sidc, setSidc] = useState<string>(defaultValue && defaultValue.length > 0 ? defaultValue : `10031000000000000000`);
	const [opened, { open, close }] = useDisclosure(false);
	const [searchValue, setSearchValue] = useState<string>("");

	const getSymbolImage = (code: string) => {
		return new Symbol.Symbol(code, {
			size: 24,
			colorMode: "Light",
			iconColor: {
				Hostile: "white",
				Friend: "white",
				Neutral: "white",
				Unknown: "white",
				Civilian: "white",
			},
		}).asSVG();
	};

	const getEmtValues = () => {
		let values;
		switch (sidc.slice(4, 6)) {
			case UNIT_SYMBOLSET_VALUE:
				values = echelonValues;
				break;
			case EQUIPMENT_SYMBOLSET_VALUE:
				values = mobilityValues;
				break;
			case DISMOUNTED_SYMBOLSET_VALUE:
				values = leadershipValues;
				break;
			case SURFACE_SYMBOLSET_VALUE:
			case SUBSURFACE_SYMBOLSET_VALUE:
				values = towedArrayValues;
				break;
			default:
				values = [{ value: "00", label: "Unspecified" }];
		}
		return values.map((e) => ({
			...e,
			image: getSymbolImage(`10${sidc[2]}${sidc[3]}${sidc.slice(4, 6)}00${e.value}0000000000`),
		}));
	};

	useEffect(() => {
		if (defaultValue && defaultValue.length > 0) {
			const newSidc = replaceAt(defaultValue || "", 3, side == 0 ? "3" : "6");
			setSidc(newSidc);
		} else {
			setSidc(`100${side == 0 ? "3" : "6"}1000000000000000`);
		}
	}, [defaultValue]);

	return (
		<Flex style={{ display: "flex", justifyContent: "flex-start", width: "70%" }}>
			<Modal opened={opened} onClose={close} title="Pick symbol" className={classes.modal}>
				<Flex style={{ alignItems: "center", justifyContent: "space-evenly", width: "100%", flexDirection: "row" }}>
					<Flex style={{ flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1rem", width: "20rem" }}>
						<img
							src={new Symbol.Symbol(sidc, {
								size: 512,
								colorMode: "Light",
								iconColor: {
									Hostile: "white",
									Friend: "white",
									Neutral: "white",
									Unknown: "white",
									Civilian: "white",
								},
							})
								.asCanvas()
								.toDataURL()}
							style={{ width: "72px", height: "auto" }}
						></img>
						<InputBase
							label="SIDC"
							value={sidc}
							component={IMaskInput}
							mask="00-0-0-00-0-0-00-000000-00-00"
							placeholder="00000000000000000000"
							onAccept={(value, mask) => {
								const obfuscatedSidc = value.replace(/-/g, "");
								if (obfuscatedSidc.length <= 20) {
									setSidc(obfuscatedSidc);
								}
							}}
							onPaste={(e) => {
								const pastedValue = e.clipboardData.getData("text/plain");
								const obfuscatedSidc = pastedValue.replace(/-/g, "");
								if (obfuscatedSidc.length <= 20) {
									setSidc(obfuscatedSidc);
								}
							}}
							onCopy={() => {
								navigator.clipboard.writeText(sidc);
							}}
							style={{ width: "100%" }}
						/>

						<Autocomplete
							label="Search"
							itemComponent={SelectItem}
							value={searchValue}
							limit={searchValue.length > 0 ? Infinity : 100}
							className={classes.select}
							filter={(value, item) => item.label.toLowerCase().includes(value.toLowerCase().trim())}
							dropdownPosition="bottom"
							maxDropdownHeight={300}
							withinPortal
							data={Object.keys(ms2525d)
								.sort()
								.map((key) => {
									const icon = ms2525d[key]["mainIcon"] || [];
									return icon.map((item: Record<string, string>) => {
										let text = item.Entity;
										if (item["Entity Type"]) text += ` - ${item["Entity Type"]}`;
										if (item["Entity Subtype"]) text += ` - ${item["Entity Subtype"]}`;
										return {
											key: key + item.Code,
											value: `1003${key}0000${item.Code}0000`,
											label: text,
											image: getSymbolImage(`1003${key}0000${item.Code}0000`),
										};
									});
								})
								.flat()}
							onItemSubmit={(item) => {
								if (item && item.value.length === 20) {
									setSidc((prev) => {
										let newSidc;
										const symbolSetValue = item.value.slice(4, 6);
										const mainIconValue = item.value.slice(10, 16);
										newSidc = replaceAt(prev, 4, symbolSetValue);
										newSidc = replaceAt(newSidc, 10, mainIconValue);
										newSidc = replaceAt(newSidc, 16, "0000");
										return newSidc;
									});
									setSearchValue(item.label);
								}
							}}
							onChange={(setValue) => {
								setSearchValue(setValue);
							}}
						/>

						<Button
							style={{ marginTop: "2rem" }}
							onClick={() => {
								onChange(sidc);
								close();
							}}
						>
							Confirm
						</Button>
					</Flex>
					<Flex style={{ alignItems: "center", justifyContent: "center", gap: "1rem", flexDirection: "column", width: "32rem" }}>
						<Select
							label="Context"
							itemComponent={SelectItem}
							data={contextValues.map((item) => ({
								value: item.value,
								label: item.label,
								image: getSymbolImage(`10${item.value}${sidc[3]}${sidc.slice(4, 6)}00000000000000`),
							}))}
							value={sidc[2]}
							className={classes.select}
							searchable
							onChange={(value) => {
								if (value) {
									const newSidc = replaceAt(sidc, 2, value);
									setSidc(newSidc);
								}
							}}
						/>

						<Select
							label="Standard Identity"
							itemComponent={SelectItem}
							data={sidValues.map((item) => ({
								value: item.value,
								label: item.label,
								image: getSymbolImage(`10${sidc[2]}${item.value}${sidc.slice(4, 6)}00000000000000`),
							}))}
							value={sidc[3]}
							className={classes.select}
							searchable
							onChange={(value) => {
								if (value) {
									const newSidc = replaceAt(sidc, 3, value);
									setSidc(newSidc);
								}
							}}
						/>

						<Select
							label="Symbol Set"
							itemComponent={SelectItem}
							data={Object.keys(ms2525d).map((key) => {
								return {
									value: key,
									label: ms2525d[key].name,
									image: getSymbolImage(`10${sidc[2]}${sidc[3]}${key}00000000000000`),
								};
							})}
							value={sidc.slice(4, 6)}
							className={classes.select}
							searchable
							onChange={(value) => {
								if (value) {
									const newSidc = replaceAt(sidc, 4, value);
									setSidc(newSidc);
								}
							}}
						/>

						<Select
							label="Status"
							itemComponent={SelectItem}
							data={statusValues.map((item) => {
								return {
									value: item.value,
									label: item.label,
									image: getSymbolImage(`10${sidc[2]}${sidc[3]}${sidc.slice(4, 6)}${item.value}0000000000`),
								};
							})}
							value={sidc[6]}
							className={classes.select}
							searchable
							onChange={(value) => {
								if (value) {
									const newSidc = replaceAt(sidc, 6, value);
									setSidc(newSidc);
								}
							}}
						/>

						<Select
							label="Headquarters/Task force/Dummy"
							itemComponent={SelectItem}
							data={HQTFDummyValues.map((item) => {
								return {
									value: item.value,
									label: item.label,
									image: getSymbolImage(`10${sidc[2]}${sidc[3]}${sidc.slice(4, 6)}${sidc[6]}${item.value}00000000`),
								};
							})}
							value={sidc[7]}
							className={classes.select}
							searchable
							onChange={(value) => {
								if (value) {
									const newSidc = replaceAt(sidc, 7, value);
									setSidc(newSidc);
								}
							}}
						/>

						<Select
							label="Echelon/Mobility/Towed array"
							itemComponent={SelectItem}
							data={getEmtValues()}
							value={sidc.slice(8, 10)}
							className={classes.select}
							searchable
							onChange={(value) => {
								if (value) {
									const newSidc = replaceAt(sidc, 8, value);
									setSidc(newSidc);
								}
							}}
						/>

						<Select
							label="Main icon"
							itemComponent={SelectItem}
							data={(ms2525d[sidc.slice(4, 6)]?.["mainIcon"] || []).map((item: Record<string, string>) => {
								let text = item.Entity;
								if (item["Entity Type"]) text += ` - ${item["Entity Type"]}`;
								if (item["Entity Subtype"]) text += ` - ${item["Entity Subtype"]}`;
								return {
									value: item.Code,
									label: text,
									image: getSymbolImage(`10${sidc[2]}${sidc[3]}${sidc.slice(4, 6)}0000${item.Code}0000`),
								};
							})}
							value={sidc.slice(10, 16)}
							className={classes.select}
							searchable
							onChange={(value) => {
								if (value) {
									const newSidc = replaceAt(sidc, 10, value);
									setSidc(newSidc);
								}
							}}
						/>

						<Select
							label="Modifier one"
							itemComponent={SelectItem}
							data={(ms2525d[sidc.slice(4, 6)]?.["modifier1"] || []).map((item: Record<string, string>) => {
								return {
									value: item.Code,
									label: item["First Modifier"],
									image: getSymbolImage(`10${sidc[2]}${sidc[3]}${sidc.slice(4, 6)}0000000000${item.Code}00`),
								};
							})}
							value={sidc.slice(16, 18)}
							className={classes.select}
							searchable
							onChange={(value) => {
								if (value) {
									const newSidc = replaceAt(sidc, 16, value);
									setSidc(newSidc);
								}
							}}
						/>

						<Select
							label="Modifier two"
							itemComponent={SelectItem}
							data={(ms2525d[sidc.slice(4, 6)]?.["modifier2"] || []).map((item: Record<string, string>) => {
								return {
									value: item.Code,
									label: item["Second Modifier"],
									image: getSymbolImage(`10${sidc[2]}${sidc[3]}${sidc.slice(4, 6)}000000000000${item.Code}`),
								};
							})}
							value={sidc.slice(18, 20)}
							className={classes.select}
							searchable
							onChange={(value) => {
								if (value) {
									const newSidc = replaceAt(sidc, 18, value);
									setSidc(newSidc);
								}
							}}
						/>
					</Flex>
				</Flex>
			</Modal>

			<Group position="left">
				<Button onClick={open}>Pick symbol</Button>
				<img
					src={new Symbol.Symbol(replaceAt(defaultValue || "", 3, side == 0 ? "3" : "6"), {
						size: 512,
						colorMode: "Light",
						iconColor: {
							Hostile: "white",
							Friend: "white",
							Neutral: "white",
							Unknown: "white",
							Civilian: "white",
						},
					})
						.asCanvas()
						.toDataURL()}
					style={{ width: "32px", height: "auto" }}
					alt="Symbol"
				/>
			</Group>
		</Flex>
	);
};

const useStyles = createStyles((theme) => ({
	modal: {
		"& .mantine-Modal-body": {
			backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.colors.light[0],
			display: "flex",
			flexDirection: "column",
			gap: "1rem",
			padding: "1rem 1rem 2rem 1rem!important",
		},

		"& .mantine-Modal-inner": {
			paddingLeft: "10vw",
			paddingRight: "10vw",
		},

		"& .mantine-Modal-content": {
			flexGrow: 1,
		},

		"& .mantine-InputWrapper-label": {
			color: theme.colorScheme === "dark" ? theme.colors.dark[2] : theme.colors.light[2],
		},
	},

	select: {
		width: "100%",
		// marginBottom: "1rem",
		// "& .mantine-Select-input": {
		// 	padding: "0.5rem",
		// },
	},
}));
